import React, { useState, useEffect, useCallback, useRef } from "react";
import { ArrowDownTrayIcon } from "@heroicons/react/24/outline";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { MultiSelect } from "@/components/MultiSelect/MultiSelect";
import type { VouchersReportFilters } from "../../hooks/useVouchersReport";

interface VouchersReportFiltersProps {
  filters: VouchersReportFilters;
  isExporting: boolean;
  onFilterChange: (
    key: keyof VouchersReportFilters,
    value: string | string[] | undefined,
  ) => void;
  onExportCsv: () => void;
}

const STATUS_OPTIONS = [
  { value: "open", label: "Open" },
  { value: "partial", label: "Partial" },
  { value: "fully_redeemed", label: "Fully Redeemed" },
  { value: "expired", label: "Expired" },
  { value: "forfeited", label: "Forfeited" },
];

const VouchersReportFilters: React.FC<VouchersReportFiltersProps> = ({
  filters,
  isExporting,
  onFilterChange,
  onExportCsv,
}) => {
  const [searchTerm, setSearchTerm] = useState(filters.searchTerm ?? "");
  const [statuses, setStatuses] = useState<string[]>(filters.statuses ?? []);

  const debouncedSearchTerm = useDebouncedValue(searchTerm, 500);
  const isMounted = useRef(false);

  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
      return;
    }
    onFilterChange("searchTerm", debouncedSearchTerm || undefined);
  }, [debouncedSearchTerm, onFilterChange]);

  const handleStatusChange = (value: string[]) => {
    setStatuses(value);
    onFilterChange("statuses", value.length ? value : undefined);
  };

  const handleClearFilters = useCallback(() => {
    setSearchTerm("");
    setStatuses([]);
    onFilterChange("searchTerm", undefined);
    onFilterChange("statuses", undefined);
  }, [onFilterChange]);

  const hasActiveFilters = searchTerm || statuses.length;

  return (
    <div className="mb-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        <div>
          <Label htmlFor="searchTerm">Search</Label>
          <Input
            className="mt-1.5 bg-white"
            id="searchTerm"
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Voucher, company..."
            type="text"
            value={searchTerm}
          />
        </div>

        <div>
          <Label>Status</Label>
          <MultiSelect
            className="mt-1.5"
            onChange={handleStatusChange}
            options={STATUS_OPTIONS}
            placeholder="All Statuses"
            value={statuses}
          />
        </div>

        <div className="flex items-end gap-2">
          <Button
            className="border-gray-300 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
            disabled={!hasActiveFilters}
            onClick={handleClearFilters}
            variant="outline"
          >
            Clear Filters
          </Button>
          <Button
            className="bg-green-600 hover:bg-green-700"
            disabled={isExporting}
            onClick={onExportCsv}
          >
            <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
            {isExporting ? "Exporting..." : "Export CSV"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default VouchersReportFilters;
